import { Link } from '@chakra-ui/next-js';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Fragment } from 'react';
import { address, brand, config, email, getNavLinks, getPrivacyPages } from '@/shared/constants';
import { DefaultContainer, LocalprofLogo } from '@/shared/ui';
import { localStorage } from '@/shared/utils';
import SocialMediaLinks from './SocialMediaLinks';

const linkBoxStyles = {
  alignItems: 'center',
  gap: 'sm',
  justifyContent: 'center',
};

const Footer = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const isShowProducts = localStorage.getItem('isShowProducts') !== 'false';
  const navLinks = getNavLinks(t);
  const privacyPages = getPrivacyPages(t);

  let currentLinks = [
    ...navLinks,
    { title: t('common.footer.navigation.integrations'), href: '/integrations' },
    { title: t('common.footer.navigation.documentation'), href: config.docAppUrl },
  ];

  if (!isShowProducts) {
    currentLinks = navLinks.filter((item) => item.title !== t('common.footer.navigation.products'));
  }

  const isStore = router.pathname.includes('/store');

  return (
    <DefaultContainer
      alignItems='center'
      bg='bg.dark'
      containerProps={{
        as: 'footer',
      }}
      flexDir={{ base: 'column-reverse', md: 'row' }}
      gap='md'
      justifyContent='space-between'
    >
      <Flex
        alignItems='center'
        flexDir='column'
      >
        <Flex
          _hover={{
            transform: 'scale(1.01)',
          }}
          alignItems='center'
          as={Link}
          gap='sm'
          href='/'
        >
          <LocalprofLogo
            aria-label='Home'
            boxSize={8}
            color='icon.light'
          />

          <Text
            color='text.light'
            fontSize='xl'
            fontWeight='semibold'
          >
            {brand}
          </Text>
        </Flex>

        <Flex
          alignItems='center'
          flexDir={{ base: 'row', md: 'column' }}
          mt='sm'
        >
          <Text color='text.light'>
            {new Date().getFullYear()} {brand} LLC.
          </Text>
          <Text
            color='text.light'
            ml={{ base: 2, md: 0 }}
          >
            {t('common.footer.allRightsReserved')}
          </Text>
        </Flex>
      </Flex>

      <Flex
        as='nav'
        flexDir='column'
        gap='sm'
      >
        {!isStore && (
          <Flex
            as='ul'
            {...linkBoxStyles}
            flexWrap='wrap'
          >
            {currentLinks.map(({ title, items, href }) => {
              if (items) {
                return (
                  <Fragment key={title}>
                    {items.map(({ name, href }) => (
                      <Box
                        key={name}
                        as='li'
                        listStyleType='none'
                      >
                        <Link
                          color='text.light'
                          href={href}
                        >
                          {name}
                        </Link>
                      </Box>
                    ))}
                  </Fragment>
                );
              }
              return (
                <Box
                  key={title}
                  as='li'
                  listStyleType='none'
                >
                  <Link
                    color='text.light'
                    href={href!}
                  >
                    {title}
                  </Link>
                </Box>
              );
            })}
          </Flex>
        )}
        <Flex
          as='ul'
          {...linkBoxStyles}
          flexWrap='wrap'
        >
          {privacyPages.map(({ name, url }) => (
            <Box
              key={name}
              as='li'
              listStyleType='none'
            >
              <Link
                color='text.light'
                href={`/${url}`}
              >
                {name}
              </Link>
            </Box>
          ))}
        </Flex>
      </Flex>

      <Flex
        flexDir='column'
        gap='sm'
      >
        <Text
          color='text.light'
          maxW={{ base: 'full', md: '16rem' }}
          textAlign='center'
        >
          {address}
        </Text>
        <Link
          color='text.light'
          fontSize='lg'
          fontWeight='semibold'
          href={`mailto:${email}`}
          textAlign='center'
        >
          {email}
        </Link>
        <SocialMediaLinks />
      </Flex>
    </DefaultContainer>
  );
};

export default Footer;
